/**
 * Log-frequency axis ticks — generation, positioning, and collision-avoiding
 * label placement. Ported from the web room-charts drawer (`chartDraw.ts`),
 * which already handles both a narrow band (20–200 Hz) and a wide band
 * (200 Hz–20 kHz) correctly via per-decade major-tick rules. Renderers draw a
 * gridline for every tick in `tickPositions` and a text label only for the
 * subset `placeTickLabels` returns — majors are always labeled, minors only
 * when they don't collide with an already-placed label.
 */

export interface LogTick {
  f: number;
  major: boolean;
}

export interface PositionedTick extends LogTick {
  x: number;
}

export interface LabeledTick extends PositionedTick {
  label: string;
}

/** Dense multi-decade tick set, filtered to [fMin, fMax]. */
export function computeLogTicks(fMin: number, fMax: number): LogTick[] {
  const ticks: LogTick[] = [];
  const push = (f: number, major: boolean) => {
    if (f >= fMin && f <= fMax) ticks.push({ f, major });
  };
  for (let f = 10; f < 100; f += 10) push(f, f === 10);
  for (let f = 100; f < 1000; f += 100) push(f, f === 100);
  for (let f = 1000; f <= 20000; f += 1000) push(f, f === 1000 || f === 10000 || f === 20000);
  return ticks;
}

export function tickPositions(ticks: LogTick[], xOf: (f: number) => number): PositionedTick[] {
  return ticks.map((t) => ({ ...t, x: xOf(t.f) }));
}

/** Evenly-spaced linear ticks in [lo, hi] on a fixed step (dB / seconds / α
 *  y-axes — none of these are log scales, so they don't go through
 *  `computeLogTicks`). */
export function linearTicks(lo: number, hi: number, step: number): number[] {
  if (!(step > 0) || !(hi >= lo)) return [];
  const out: number[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) out.push(v);
  return out;
}

/** Pick a "nice" step (1/2/5 × 10ⁿ) from `lo`/`hi` so the resulting
 *  tick count never exceeds `maxTicks` — keeps the 1-2-5 rounding charts
 *  already use, but caps how many horizontal gridlines a dB (or any linear)
 *  axis draws so it stays legible. Returns the chosen step plus the next
 *  coarser nice value so callers can mark a major every ~two ticks. */
export function niceStep(span: number, maxTicks: number): number {
  if (!(span > 0) || !(maxTicks > 0)) return 1;
  const raw = span / maxTicks;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const niceNorm = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return niceNorm * mag;
}

export interface NiceLinearTicks {
  values: number[];
  step: number;
  majorInterval: number;
}

/** Linear ticks in [lo, hi] on a nice step capped to ~`maxTicks` lines. */
export function niceLinearTicks(lo: number, hi: number, maxTicks = 8): NiceLinearTicks {
  const span = hi - lo;
  const step = niceStep(span, maxTicks);
  const mag = 10 ** Math.floor(Math.log10(step));
  const norm = step / mag;
  const nextNorm = norm <= 1 ? 2 : norm <= 2 ? 5 : 10;
  const majorInterval = nextNorm * mag;
  const stepLo = Math.floor(lo / step) * step;
  const stepHi = Math.ceil(hi / step) * step;
  return { values: linearTicks(stepLo, stepHi, step), step, majorInterval };
}

/**
 * Thin an evenly spaced row of tick labels (a linear time or distance axis)
 * to every k-th one, k the smallest step at which the widest label clears its
 * neighbour by `minGap`. The first label always survives, so the axis still
 * starts at its origin.
 *
 * `extent` is a label's footprint along the axis: its measured width on a
 * horizontal axis, the font size on a vertical one. A row whose labels already
 * clear each other comes back unchanged — this only ever removes, and only
 * when the type is too big for the spacing the scale gave it.
 */
export function thinEvenTickLabels<T extends { label: string }>(
  ticks: readonly T[],
  position: (tick: T) => number,
  extent: (label: string) => number,
  minGap = 6,
): T[] {
  if (ticks.length < 2) return [...ticks];
  const spacing = Math.abs(position(ticks[1]!) - position(ticks[0]!));
  if (!(spacing > 0)) return ticks.slice(0, 1);
  const widest = ticks.reduce((m, t) => Math.max(m, extent(t.label)), 0);
  const every = Math.max(1, Math.ceil((widest + minGap) / spacing));
  return ticks.filter((_, i) => i % every === 0);
}

export function formatHz(f: number): string {
  return f >= 1000 ? `${f / 1000}k` : `${f}`;
}

/**
 * Majors are always placed. Minors are inserted (in x order) only when they
 * don't overlap an already-placed label's bounding box plus `minGap`.
 */
export function placeTickLabels(
  positioned: PositionedTick[],
  measureWidth: (label: string) => number,
  opts?: { minGap?: number; formatLabel?: (f: number) => string },
): LabeledTick[] {
  const minGap = opts?.minGap ?? 6;
  const formatLabel = opts?.formatLabel ?? formatHz;

  const infos = positioned.map((t) => {
    const label = formatLabel(t.f);
    return { ...t, label, w: measureWidth(label) };
  });

  const placed = infos.filter((t) => t.major).sort((a, b) => a.x - b.x);
  for (const cand of infos) {
    if (cand.major) continue;
    let i = 0;
    while (i < placed.length && placed[i]!.x < cand.x) i++;
    const left = placed[i - 1];
    const right = placed[i];
    if (left && cand.x - cand.w / 2 < left.x + left.w / 2 + minGap) continue;
    if (right && cand.x + cand.w / 2 > right.x - right.w / 2 - minGap) continue;
    placed.splice(i, 0, cand);
  }

  return placed.map(({ f, major, x, label }) => ({ f, major, x, label }));
}
